import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, map, shareReplay, tap } from 'rxjs/operators';

import { circleMarker, LatLng, Polygon, polygon, Polyline, polyline } from 'leaflet';
// Models
import { FlatPathNode, COLORS, MultipleApiResponse, RegionRef, RegionMap, TrackSeqRef, LocationRef, TrackSeq, Wgs84Point } from '@mocca-technologies/model';

import { ALL_REGION_MAPS, REGION_MAPS } from '../utils/routing/routes.model';
import { environment } from 'src/environments/environment';

@Injectable()
export class RegionMapsService {
    private readonly DEBUG = true;

    private _waitingRequest: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    waitingRequest$: Observable<boolean> = this._waitingRequest.asObservable();

    private _selectedRegionMapData: BehaviorSubject<RegionMap> = new BehaviorSubject(null);
    selectedRegionMapData$: Observable<RegionMap> = this._selectedRegionMapData
        .asObservable()
        .pipe(
            filter((data) => data !== null),
            distinctUntilChanged((prev, curr) => prev.uniqueId === curr.uniqueId),
            tap((regionMap) => this.DEBUG && !environment.production && console.log('selectedRegionGroupData', regionMap)),
            shareReplay(1)
        );

    constructor(private http: HttpClient) {}

    private flattenList(regionMapsList: RegionMap[]): FlatPathNode[] {
        // Sorting list alphabetically
        regionMapsList.sort((a, b) => (a.path > b.path ? 1 : -1));

        // Keep track of paths to avoid duplicated
        const pathHistory = [];

        return regionMapsList.reduce((flatList, regionMap) => {
            // Split path into directories
            const fullPath = regionMap.path.split('/');
            const currentPath: string[] = [];

            fullPath.forEach((dir, index) => {
                // Update current path
                currentPath.push(dir);
                const path = currentPath.join('/');

                // Ignore path if it's already appended into the tree list
                if (!pathHistory.includes(path)) {
                    const isLeaf = fullPath.length === currentPath.length;
                    const flatObject = {
                        name: dir,
                        level: index,
                        matchFilter: true,
                        dataRef: isLeaf && regionMap,
                        expandable: !isLeaf,
                        badgeValue: !isLeaf && 0
                    };

                    flatList.push(flatObject);
                    pathHistory.push(path);
                }
            });

            return flatList;
        }, []);
    }

    getRegionsList(): Observable<HttpResponse<MultipleApiResponse>> {
        this._waitingRequest.next(true);

        return this.http.get<MultipleApiResponse>(ALL_REGION_MAPS, {
          observe: 'response',
          withCredentials: true
         }).pipe(
            finalize(() => this._waitingRequest.next(false)),
            tap((response: HttpResponse<MultipleApiResponse>) => this.DEBUG && !environment.production && console.log('getAll', response?.body?.responseObject as RegionMap[])),
            catchError((error: HttpErrorResponse) => {
                this._waitingRequest.next(false);
                throw error;
            })
        );
    }

    getFlattenedList(): Observable<FlatPathNode[]> {
        this._waitingRequest.next(true);

        return this.http.get<MultipleApiResponse>(ALL_REGION_MAPS, {
            observe: 'response',
            withCredentials: true
         }).pipe(
            map((res: HttpResponse<MultipleApiResponse>) => {

                if (res?.status === 204){
                    return [];
                }

                const element = res?.body?.responseObject as RegionMap[];
                return element.map((regionMap) => {
                    regionMap.path ?? '';
                    regionMap.path += `/${regionMap.name}`;

                    regionMap.isChecked = false;
                    regionMap.isIndeterminate = false;

                    // Regions
                    regionMap.regions = regionMap.regions.map((regionRef: RegionRef) => {
                        const rset = (regionRef.region.rset as Wgs84Point[]).map(
                            (e) => new LatLng(e.latitude, e.longitude)
                        );
                        const polygonLayer: Polygon<Wgs84Point> = regionRef.region.polygonLayer ? regionRef?.region?.polygonLayer : polygon(rset);
                        const regionRefCopy: RegionRef = { ...regionRef, region: { ...regionRef.region, polygonLayer } };

                        return regionRefCopy;
                    });

                    // Locations
                    regionMap.locations = regionMap.locations.map((locationRef: LocationRef) => {
                        const latlng = new LatLng(
                          locationRef?.location?.centre?.latitude,
                          locationRef?.location?.centre?.longitude
                        );
                        const polygonLayer = locationRef?.location?.polygonLayer ? locationRef?.location?.polygonLayer : circleMarker(latlng);
                        const locationRefCopy: LocationRef = { ...locationRef, location: { ...locationRef.location, polygonLayer, latlng } };
                        return locationRefCopy;
                    });

                    // Tracks
                    regionMap.tracks = regionMap.tracks.map((trackRef: TrackSeqRef) => {
                        const trackSeq = (trackRef.trackSequence.tsequence as Array<Wgs84Point>).map(
                            (e) => new LatLng(e.latitude, e.longitude)
                        );
                        const polygonLayer: Polygon = trackRef.trackSequence.polygonLayer ? trackRef.trackSequence.polygonLayer : polygon(trackSeq);
                        const trackRefCopy: TrackSeqRef = { ...trackRef, trackSequence: { ...trackRef.trackSequence, polygonLayer } };
                        return trackRefCopy;
                    });

                    return regionMap;
                });
            }),
            finalize(() => this._waitingRequest.next(false)),
            tap((regionMaps) => this.DEBUG && !environment.production && console.log('getAll', regionMaps)),
            map<RegionMap[], FlatPathNode[]>((regionMapsList) => this.flattenList(regionMapsList)),
            catchError((error: HttpErrorResponse) => { throw error; return []; })
        );
    }

    selectRegionGroup({ dataRef }: FlatPathNode): void {
        this._selectedRegionMapData.next(dataRef);
    }

    getHexColor(name: string): string {
        return getComputedStyle(document.documentElement).getPropertyValue('--ion-color-' + name);
    }

    getNameColor(idx: number): string {
        const colorsLen = Object.keys(COLORS).length / 2;
        return COLORS[idx % colorsLen];
    }

    createRegion(regionMap: RegionMap): Observable<HttpResponse<MultipleApiResponse>> {

        // helpers (keep these near the function or in a utils file)
        const normalizeRing = (latlngs: any): any[] => {
            // Leaflet Polygon: [LatLng[]]
            // Leaflet MultiPolygon: [LatLng[][]]
            if (!Array.isArray(latlngs) || latlngs.length === 0) return [];

            const a = latlngs[0];
            if (!Array.isArray(a) || a.length === 0) return [];

            // MultiPolygon style: a[0] is the ring (LatLng[])
            if (Array.isArray(a[0])) return a[0];

            // Polygon style: a is already the ring (LatLng[])
            return a;
        };

        const toVertexDto = (v: any) => ({
            latitude: (v?.lat ?? v?.latitude)?.toString(),
            longitude: (v?.lng ?? v?.longitude)?.toString(),
        });

        const getVertices = (regionResource: any) => {
            const rset = regionResource?.region?.rset;

            // Use rset only when it has points
            if (Array.isArray(rset) && rset.length > 0) return rset;

            // Fallback to polygonLayer when rset is empty/missing
            const latlngs = regionResource?.region?.polygonLayer?.getLatLngs?.();
            return normalizeRing(latlngs);
        };

        // your DTO
        const regionMapDto = {
            name: regionMap?.name,
            description: regionMap?.description,
            path: regionMap?.path,

            regions: (regionMap?.regions ?? []).map((regionResource: RegionRef) => ({
                regionName: regionResource?.regionName,
                regionUUID: regionResource?.regionUUID,
                referenceRegionName: regionResource?.region?.name,
                minAltitude: regionResource?.minAltitude,
                maxAltitude: regionResource?.maxAltitude,

                region: {
                    name: regionResource?.region?.name,
                    description: regionResource?.region?.description,
                    searchKeys: regionResource?.region?.searchKeys,
                    minAltitude: regionResource?.region?.minAltitude,
                    maxAltitude: regionResource?.region?.maxAltitude,
                    radius: regionResource?.region?.radius,
                    baseAltitude: regionResource?.region?.baseAltitude,
                    timeZone: regionResource?.region?.timeZone,

                    rset: getVertices(regionResource).map(toVertexDto),

                    holeList: regionResource?.region?.holeList,
                },
            })),
        };


        return this.http.post<MultipleApiResponse>(
            REGION_MAPS,
            { regionMap: JSON.stringify(regionMapDto) },
            { observe: 'response', withCredentials: true }
        ).pipe(
            catchError((error: HttpErrorResponse) => throwError(() => error))
        );
    }
}
